import { useState } from "react";
import { Layout } from "../components/Layout.js";
import { Button } from "../components/Button.js";
import { useScreener } from "../state.js";
import { useAudio } from "../audio/AudioProvider.js";
import { useFlow } from "./useFlow.js";

export function SetVolume() {
  const { config } = useScreener();
  const { ensureEngine, volume, setVolume } = useAudio();
  const { go } = useFlow();
  const [playing, setPlaying] = useState(false);
  const [played, setPlayed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function playSample() {
    if (!config) return;
    setError(null);
    try {
      const engine = await ensureEngine(config.audio, config.din);
      setPlaying(true);
      // Comfortable-level reference tone in each ear (1 kHz).
      await engine.playTone(1000, 12, "left", 0.8);
      await engine.playTone(1000, 12, "right", 0.8);
      setPlayed(true);
    } catch {
      setError("We couldn't play the sample. Please try again.");
    } finally {
      setPlaying(false);
    }
  }

  return (
    <Layout step="set-volume">
      <h2 className="center">Set your volume</h2>
      <p className="muted center screen-copy">
        Play the sample tone and adjust the slider until it is comfortable and
        clearly audible. Keep this volume for the whole test.
      </p>

      <div className="volume-panel">
        <div className="sample-action">
          <Button variant="secondary" onClick={playSample} disabled={playing}>
            {playing ? "Playing sample…" : "▶ Play sample tone"}
          </Button>
        </div>

        <div className="volume">
          <span className="volume-icon" aria-hidden>
            <SpeakerIcon quiet />
          </span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.02}
            value={volume}
            onChange={(e) => setVolume(Number(e.target.value))}
            aria-label="Volume"
          />
          <span className="volume-icon" aria-hidden>
            <SpeakerIcon />
          </span>
        </div>

        <p className="muted volume-tip">
          Tip: set your device volume to about two-thirds before adjusting here.
        </p>
      </div>

      {error ? <div className="note">{error}</div> : null}

      <div className="actions">
        <Button variant="secondary" onClick={() => go("headphones")}>
          Back
        </Button>
        <Button disabled={!played} onClick={() => go("test")}>
          Start the hearing test
        </Button>
      </div>
    </Layout>
  );
}

function SpeakerIcon({ quiet = false }: { quiet?: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
      {!quiet ? (
        <path
          d="M16 9a4 4 0 010 6M18.5 6.5a7.5 7.5 0 010 11"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      ) : null}
    </svg>
  );
}
